import { createContext, useMemo, type FC, type ReactNode, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAuctionCountdown } from 'dibs-crypto-utils/exports/useAuctionCountdown';
import { type NftItemTileContextType, useNftItemTileContext } from '../NftItemTileContext';

import {
    type ItemInternalContext_nftItem$key,
    type ItemInternalContext_nftItem$data,
} from './__generated__/ItemInternalContext_nftItem.graphql';
import {
    type ItemInternalContext_nftAuction$key,
    type ItemInternalContext_nftAuction$data,
} from './__generated__/ItemInternalContext_nftAuction.graphql';
import {
    type ItemInternalContext_nftAuctionBidderRef$key,
    type ItemInternalContext_nftAuctionBidderRef$data,
} from './__generated__/ItemInternalContext_nftAuctionBidderRef.graphql';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { type Props as NftItemTilePriceProps } from '../components/NftItemTilePrice';

type ItemInternalContextProviderProps = {
    nftItem?: ItemInternalContext_nftItem$key | null | undefined;
    nftAuction?: ItemInternalContext_nftAuction$key | null | undefined;
    nftAuctionBidderRef?: ItemInternalContext_nftAuctionBidderRef$key | null | undefined;
    children?: ReactNode;
};

type Flags<T extends string> = { [K in T]: boolean } | null;
type PriceProps = Pick<NftItemTilePriceProps, 'etherAmount' | 'etherRates'>;

type Bidder = Flags<'isLeader' | 'wonAuction' | 'lostAuction'> & {
    action: null | 'settle' | 'view' | 'bid';
    bid: { bidDate: string; bidderAddress: string | null } & PriceProps;
};
type DerivedState = {
    auction: Flags<'isCountdownFinished' | 'isCancellable' | 'isLive'>;
    user: Flags<'isAuctionCreator' | 'isAuctionSeller' | 'isItemOwner' | 'isBidder'>;
    bidder: null | Bidder;
    profile: Flags<'isItemCreator' | 'isUserProfile'>;
};
const nftItemFragment = graphql`
    fragment ItemInternalContext_nftItem on NftItem {
        creatorAddress {
            address
        }
        balanceForUser(userId: $userId) @include(if: $fetchUser) {
            balance
            ownerAddress {
                address
            }
        }
    }
`;

const nftAuctionFragment = graphql`
    fragment ItemInternalContext_nftAuction on NftAuction {
        endDate
        status
        winnerAddress {
            address
        }
        creatorAddress {
            address
        }
        sellerAddress {
            address
        }
        item {
            pdpUrl
            etherRates {
                ...NftItemTilePrice_etherRates
            }
        }
    }
`;

const nftAuctionBidderFragment = graphql`
    fragment ItemInternalContext_nftAuctionBidderRef on NftAuctionBidderRef {
        bidDate
        bidAmount {
            ether
        }
        bidderAddress {
            address
        }
        auction {
            endDate
        }
    }
`;
/**
 * computes if args are equal and not falsy. we don't want to end up assigning
 * true to flags based on comparisons where all args are null or undefined at
 * runtime. basically, if a flag starts with "if" and is a comparison, use this.
 */
const equivalent = (...args: unknown[]): boolean => !!args[0] && args.every(arg => arg === args[0]);

function deriveState(
    nftItem: ItemInternalContext_nftItem$data | null | undefined,
    nftAuction: ItemInternalContext_nftAuction$data | null | undefined,
    nftAuctionBidder: ItemInternalContext_nftAuctionBidderRef$data | null | undefined,
    { userWalletAddress, profileWalletAddress }: NftItemTileContextType,
    countdownFinished: boolean | null
): DerivedState {
    const isLive = !!nftAuction?.status?.match(/LIVE|PAUSED/);
    const isCountdownFinished = isLive && !!countdownFinished;
    const auctionCreator = nftAuction?.creatorAddress?.address;
    const auctionWinner = nftAuction?.winnerAddress?.address;
    const auctionSeller = nftAuction?.sellerAddress?.address;
    const itemCreator = nftItem?.creatorAddress?.address;
    // this flag can also be used to determine if an auction has any bids. a
    // live auction with no bids has no leader while an auction with at least
    // one bid will have a leader.
    const auctionHasLeader = !!auctionWinner;

    const isItemOwner = Boolean(
        nftItem?.balanceForUser?.balance && nftItem?.balanceForUser?.balance > 0
    );

    const user = {
        isAuctionCreator: equivalent(userWalletAddress, auctionCreator),
        isAuctionSeller: equivalent(userWalletAddress, auctionSeller),
        isItemOwner,
        isBidder: equivalent(userWalletAddress, nftAuctionBidder?.bidderAddress?.address),
    };

    const auction = {
        isCountdownFinished,
        isCancellable: user.isAuctionCreator && !auctionHasLeader,
        isLive,
    };

    const isBidderLeader = equivalent(auctionWinner, nftAuctionBidder?.bidderAddress?.address);
    const bidder: DerivedState['bidder'] = {
        isLeader: isBidderLeader,
        wonAuction: isBidderLeader && isCountdownFinished && nftAuction?.status !== 'ENDED',
        lostAuction: (!isBidderLeader && isCountdownFinished) || (!isBidderLeader && !isLive),
        action: null,
        bid: {
            bidderAddress: nftAuctionBidder?.bidderAddress?.address || null,
            bidDate: nftAuctionBidder?.bidDate || '',
            etherAmount: nftAuctionBidder?.bidAmount?.ether,
            etherRates: nftAuction?.item?.etherRates?.filter(filterNulls) || null,
        },
    };

    if (user.isBidder && bidder.wonAuction) {
        bidder.action = 'settle';
    } else if (user.isBidder && (bidder.isLeader || bidder.lostAuction)) {
        bidder.action = 'view';
    } else if (nftAuctionBidder && isLive) {
        bidder.action = 'bid';
    }

    const profile = {
        isItemCreator: equivalent(profileWalletAddress, itemCreator),
        isUserProfile: equivalent(profileWalletAddress, userWalletAddress),
    };

    const derivedState = {
        auction: nftAuction?.status ? auction : null,
        user: userWalletAddress ? user : null,
        profile: profileWalletAddress ? profile : null,
        bidder: nftAuctionBidder ? bidder : null,
    };

    return derivedState;
}

export const ItemInternalContext = createContext<DerivedState>({
    auction: null,
    user: null,
    profile: null,
    bidder: null,
});

export const ItemInternalContextProvider: FC<ItemInternalContextProviderProps> = ({
    nftItem: nftItemProp = null,
    nftAuction: nftAuctionProp = null,
    nftAuctionBidderRef: nftAuctionBidderRefProp = null,
    children,
}) => {
    const nftItem = useFragment(nftItemFragment, nftItemProp);
    const nftAuction = useFragment(nftAuctionFragment, nftAuctionProp);
    const nftAuctionBidder = useFragment(nftAuctionBidderFragment, nftAuctionBidderRefProp);
    const { countdownFinished } = useAuctionCountdown(nftAuction?.endDate, true);
    const tileContext = useNftItemTileContext();
    const state = useMemo(
        () => deriveState(nftItem, nftAuction, nftAuctionBidder, tileContext, countdownFinished),
        [countdownFinished, nftAuction, nftItem, nftAuctionBidder, tileContext]
    );

    return <ItemInternalContext.Provider value={state}>{children}</ItemInternalContext.Provider>;
};

export const useItemInternalContext = (): DerivedState => useContext(ItemInternalContext);
